import { useMemo, useState } from "react";
import type { PersonProfile } from "@sales-ops-amplify/shared";

type FieldRow = { label: string; value: string };

export function PersonFieldsTab({ profile }: { profile: PersonProfile }) {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<"person" | "company">("person");

  const personFields = useMemo((): FieldRow[] => {
    const u = profile.user;
    return [
      { label: "Name", value: profile.name },
      { label: "First name", value: profile.firstName ?? "" },
      { label: "Last name", value: profile.lastName ?? "" },
      { label: "Job title", value: profile.title ?? "" },
      { label: "Seniority", value: profile.seniority ?? "" },
      {
        label: "Departments",
        value: profile.departments?.join(", ") ?? "",
      },
      {
        label: "Sub-departments",
        value: profile.subDepartments?.join(", ") ?? "",
      },
      {
        label: "Emails",
        value: profile.emails.map((e) => e.email).join(", "),
      },
      {
        label: "Phones",
        value: profile.phones.map((p) => p.number).join(", "),
      },
      { label: "LinkedIn", value: profile.linkedinUrl ?? "" },
      {
        label: "Location",
        value: [profile.city, profile.state, profile.country]
          .filter(Boolean)
          .join(", "),
      },
      { label: "Stage", value: profile.stage ?? "" },
      { label: "Owner", value: profile.leadOwner ?? "" },
      { label: "Lifecycle", value: u?.lifecycleStage ?? "" },
      { label: "Source", value: u?.source ?? "" },
      { label: "Relationship", value: u?.relationship ?? "" },
      { label: "Notes", value: u?.notes ?? "" },
      {
        label: "External links",
        value:
          u?.externalLinks
            ?.map((l) => `${l.label}: ${l.url}`)
            .join("; ") ?? "",
      },
      { label: "Person ID", value: profile.personId },
      { label: "Contact ID", value: profile.leadId ?? "" },
      { label: "Apollo person ID", value: profile.apolloPersonId ?? "" },
    ];
  }, [profile]);

  const companyFields = useMemo((): FieldRow[] => {
    const o = profile.organization;
    return [
      { label: "Company", value: o.name },
      { label: "Domain", value: o.domain ?? "" },
      { label: "Industry", value: o.industry ?? "" },
      {
        label: "Employees",
        value: o.employeeCount != null ? String(o.employeeCount) : "",
      },
      { label: "Website", value: o.websiteUrl ?? "" },
      { label: "LinkedIn", value: o.linkedinUrl ?? "" },
      {
        label: "Keywords",
        value: o.keywords?.join(", ") ?? "",
      },
      {
        label: "Technologies",
        value: o.technologies?.join(", ") ?? "",
      },
      {
        label: "Revenue",
        value: o.annualRevenue != null ? String(o.annualRevenue) : "",
      },
      {
        label: "HQ",
        value:
          o.companyAddress ||
          [o.companyCity, o.companyState, o.companyCountry]
            .filter(Boolean)
            .join(", "),
      },
      { label: "Company ID", value: o.companyId ?? "" },
      { label: "Account ID", value: profile.accountId ?? "" },
    ];
  }, [profile]);

  const rows = (scope === "person" ? personFields : companyFields).filter(
    (r) => {
      if (!q.trim()) return true;
      const hay = `${r.label} ${r.value}`.toLowerCase();
      return hay.includes(q.trim().toLowerCase());
    },
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-md border border-border p-0.5">
          {(["person", "company"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScope(s)}
              className={
                scope === s
                  ? "rounded px-2.5 py-1 text-xs font-semibold bg-muted"
                  : "rounded px-2.5 py-1 text-xs text-muted-foreground"
              }
            >
              {s === "person" ? "Person" : "Company"}
            </button>
          ))}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search fields…"
          className="min-w-0 flex-1 rounded-md border border-border bg-bg px-3 py-1.5 text-sm"
        />
      </div>

      <div className="overflow-x-hidden rounded-lg border border-border">
        <table className="w-full table-fixed text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-border last:border-0">
                <th className="w-[34%] bg-muted/20 px-3 py-2 text-left align-top text-xs font-medium text-muted-foreground">
                  <span className="break-words">{r.label}</span>
                </th>
                <td className="px-3 py-2 align-top text-foreground">
                  <span className="break-all">{r.value || "—"}</span>
                </td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={2}
                  className="px-3 py-8 text-center text-muted-foreground"
                >
                  No matching fields
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
